import { queryOptions } from '@tanstack/react-query';

import { commentApiHandlers } from '@entities/comment/api/handlers';
import { TCommentType, TUploadData } from '@entities/comment/model/types';
import { ICommentGroup } from '@entities/message/model/types';
import { $api } from '@shared/api';
import { keysToCamel } from '@shared/helpers';
import { getCommentsPollingRoute } from '@widgets/Comments/api/routes';
import { extractUnreadPositionGuids } from '@widgets/Comments/lib/polling';

export const commentsApi = {
    getCommentListQueryOptions: ({
        commentType,
        positionGuid,
    }: {
        commentType: TCommentType;
        positionGuid: string;
    }) =>
        queryOptions({
            queryKey: ['getCommentList', positionGuid, commentType],
            queryFn: (meta) =>
                commentApiHandlers.getCommentList(
                    { signal: meta.signal },
                    { commentType, positionId: positionGuid }
                ),
            select: (data) => {
                const parsedData = keysToCamel<{
                    data: ICommentGroup[];
                }>(data?.data);
                return parsedData;
            },
            refetchOnMount: false,
        }),
    getUnreadCommentsPollingQueryOptions: ({
        estimateId,
        withIgnoreCache,
        commentType,
    }: {
        estimateId: string;
        withIgnoreCache: boolean;
        commentType: TCommentType;
    }) => {
        const commentsPollingUrl = getCommentsPollingRoute({ commentType, estimateId });

        return queryOptions({
            queryKey: ['comments-indicator', commentsPollingUrl],
            enabled: Boolean(commentsPollingUrl),
            queryFn: async (meta) => {
                const response = await $api.get(commentsPollingUrl, {
                    signal: meta.signal,
                    ...(withIgnoreCache && {
                        params: {
                            ignore_cache: 1,
                        },
                    }),
                });

                return keysToCamel<Record<string, unknown>>(response.data);
            },
            select: extractUnreadPositionGuids,
        });
    },
    saveCommentQueryOptions: ({
        positionGuid,
        commentType,
        data,
    }: {
        positionGuid: string;
        commentType: TCommentType;
        data: TUploadData;
    }) =>
        commentApiHandlers.saveNewPositionComment({ positionId: positionGuid, commentType, data }),
    deleteCommentQueryOptions: ({
        positionGuid,
        commentId,
        commentType,
    }: {
        positionGuid: string;
        commentId: string;
        commentType: TCommentType;
    }) =>
        commentApiHandlers.deletePositionComment({
            positionId: positionGuid,
            commentId,
            commentType,
        }),
};
